import React from 'react';
import Container from '@material-ui/core/Container';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';
import { AutomaticAnnotationClusterData } from '../utils/getSequenceAutomaticAnnotationsData';
import { ExcerptMultipleChart } from '../charts/ExcerptMultipleChart';
import { clusterPickSeed, pickTwoExcerptIndexes } from './clusterExcerptPicks';

const useStyles = makeStyles((theme) => ({
  root: {
    width: 800,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  title: {
    paddingBottom: theme.spacing(1),
  },
  chart: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  chartContainer: {
    display: 'flex',
    flexWrap: 'wrap',
  },
}));

type AnnotationAccumulator = {
  V: AutomaticAnnotationClusterData[];
  S: AutomaticAnnotationClusterData[];
};

type AutomaticAnnotationsChartsProps = {
  /** Seeds the single-beat picks (project_management#561): same analysis, same beats. */
  analysisId: string;
  annotations: AutomaticAnnotationClusterData[];
};

export function ClusterChartList(props: AutomaticAnnotationsChartsProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  if (!props.annotations?.length) {
    return null;
  }

  const { V, S } = props.annotations.reduce<AnnotationAccumulator>(
    (acc, item) => {
      if (item.annotationValue === 'V') {
        acc.V.push(item);
      } else if (item.annotationValue === 'S') {
        acc.S.push(item);
      }

      return acc;
    },
    { V: [], S: [] },
  );

  return (
    <>
      <Container className={classes.root} component={Paper}>
        <Typography className={classes.title} variant="h6">
          {t('clusterChartList.ventricularBeatsTitle')}
        </Typography>
        <div className={classes.chartContainer}>
          {V.map((it) => {
            if (!it.isValid) {
              return null;
            }

            const [firstPick, secondPick] = pickTwoExcerptIndexes(
              it.excerpts.length - 1,
              clusterPickSeed(props.analysisId, it.annotationValue, it.clusterNumber),
            );

            return (
              <React.Fragment key={it.clusterNumber}>
                <div className={classes.chart}>
                  <ExcerptMultipleChart
                    annotationValue={it.annotationValue}
                    clusterNumber={it.clusterNumber}
                    data={it.excerpts}
                  />
                </div>
                {it.excerpts.length > 3 && (
                  <>
                    <div className={classes.chart}>
                      <ExcerptMultipleChart
                        annotationValue={it.annotationValue}
                        clusterNumber={it.clusterNumber}
                        data={[it.excerpts[firstPick]]}
                      />
                    </div>
                    <div className={classes.chart}>
                      <ExcerptMultipleChart
                        annotationValue={it.annotationValue}
                        clusterNumber={it.clusterNumber}
                        data={[it.excerpts[secondPick]]}
                      />
                    </div>
                  </>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </Container>

      <Container className={classes.root} component={Paper}>
        <Typography className={classes.title} variant="h6">
          {t('clusterChartList.supraventricularBeatsTitle')}
        </Typography>
        <div className={classes.chartContainer}>
          {S.map((it) => {
            if (!it.isValid) {
              return null;
            }

            const [firstPick, secondPick] = pickTwoExcerptIndexes(
              it.excerpts.length - 1,
              clusterPickSeed(props.analysisId, it.annotationValue, it.clusterNumber),
            );

            return (
              <React.Fragment key={it.clusterNumber}>
                <div className={classes.chart}>
                  <ExcerptMultipleChart
                    annotationValue={it.annotationValue}
                    clusterNumber={it.clusterNumber}
                    data={it.excerpts}
                  />
                </div>
                {it.excerpts.length > 3 && (
                  <>
                    <div className={classes.chart}>
                      <ExcerptMultipleChart
                        annotationValue={it.annotationValue}
                        clusterNumber={it.clusterNumber}
                        data={[it.excerpts[firstPick]]}
                      />
                    </div>
                    <div className={classes.chart}>
                      <ExcerptMultipleChart
                        annotationValue={it.annotationValue}
                        clusterNumber={it.clusterNumber}
                        data={[it.excerpts[secondPick]]}
                      />
                    </div>
                  </>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </Container>
    </>
  );
}
